import api from './api';
import { fetchAndSave } from '../utils/downloadResponse';
import { Expense, ApiResponse, Pagination } from '../types';

export interface ExpenseListResponse {
  expenses: Expense[];
  pagination: Pagination;
}

export interface PendingExpenseListResponse {
  expenses: Expense[];
  pendingTotal: number;
  pagination: Pagination;
}

export interface ExpenseStats {
  totalCount: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  totalApprovedAmount: number;
  totalPendingAmount: number;
}

/** Payload for the JSON bulk-create endpoint. Rows are pre-validated by the
 *  frontend; the backend re-validates defensively and rejects the whole
 *  batch with per-row errors if any row fails. */
export interface BulkExpenseRowInput {
  rowNumber: number;
  title: string;
  amount: number;
  /** ISO 8601 date string. */
  date: string;
  categoryId?: string;
  subCategoryId?: string;
  notes?: string;
}

export interface BulkRowsSuccess {
  batchId: string;
  created: number;
  requested: number;
}

/** Thrown when the server rejects the batch for per-row validation reasons.
 *  Carries the parsed error list so the caller can re-mark rows in the
 *  review UI. */
export class BulkRowsValidationError extends Error {
  constructor(public readonly errors: Array<{ rowNumber: number; errorMessage: string }>, message: string) {
    super(message);
    this.name = 'BulkRowsValidationError';
  }
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

export const getExpenses = async (params?: {
  status?: string;
  categoryId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}): Promise<ExpenseListResponse> => {
  const sp = new URLSearchParams();
  if (params?.status) sp.append('status', params.status);
  if (params?.categoryId) sp.append('categoryId', params.categoryId);
  if (params?.search) sp.append('search', params.search);
  if (params?.dateFrom) sp.append('dateFrom', params.dateFrom);
  if (params?.dateTo) sp.append('dateTo', params.dateTo);
  if (params?.page) sp.append('page', params.page.toString());
  if (params?.limit) sp.append('limit', params.limit.toString());

  const response = await api.get<ApiResponse<ExpenseListResponse>>(`/expenses?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getExpenseById = async (id: string): Promise<Expense> => {
  const response = await api.get<ApiResponse<Expense>>(`/expenses/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const createExpense = async (data: FormData): Promise<Expense> => {
  const response = await api.post<ApiResponse<Expense>>('/expenses', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const updateExpense = async (id: string, data: FormData): Promise<Expense> => {
  const response = await api.put<ApiResponse<Expense>>(`/expenses/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const deleteExpense = async (id: string): Promise<void> => {
  const response = await api.delete<ApiResponse>(`/expenses/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
};

// ─── CSV Bulk Upload ─────────────────────────────────────────────────────────

/** Submit a client-parsed array of expense rows. The backend is pure CRUD
 *  — no file upload, no multer. On per-row validation failure the server
 *  returns 400 with `data.errors` which we unwrap into
 *  `BulkRowsValidationError` so the dialog can re-mark the offending rows. */
export const bulkCreateRows = async (rows: BulkExpenseRowInput[]): Promise<BulkRowsSuccess> => {
  try {
    const response = await api.post<ApiResponse<BulkRowsSuccess>>('/expenses/bulk-rows', { rows });
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data!;
  } catch (err: any) {
    // Axios 4xx responses throw by default. Pull out the per-row errors so
    // the UI can surface them inline instead of a flat message.
    const data = err?.response?.data;
    if (Array.isArray(data?.data?.errors)) {
      throw new BulkRowsValidationError(data.data.errors, data.message || 'Validation failed');
    }
    throw new Error(data?.message || err?.message || 'Bulk upload failed');
  }
};



// ─── Stats ───────────────────────────────────────────────────────────────────

export const getExpenseStats = async (params?: {
  schoolId?: string;
}): Promise<ExpenseStats> => {
  const sp = new URLSearchParams();
  if (params?.schoolId) sp.append('schoolId', params.schoolId);
  const response = await api.get<ApiResponse<ExpenseStats>>(`/expenses/stats?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

// ─── Approval ────────────────────────────────────────────────────────────────

export interface ApprovalSubmitter {
  _id: string;
  name: string;
  email?: string;
}

export const getApprovalContext = async (params?: {
  search?: string;
  limit?: number;
}): Promise<{ submitters: ApprovalSubmitter[] }> => {
  const sp = new URLSearchParams();
  if (params?.search) sp.append('search', params.search);
  if (params?.limit) sp.append('limit', params.limit.toString());
  const response = await api.get<ApiResponse<{ submitters: ApprovalSubmitter[] }>>(
    `/expenses/approval-context?${sp.toString()}`
  );
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getPendingExpenses = async (params?: {
  status?: string;
  createdBy?: string;
  batchId?: string;
  categoryId?: string;
  subCategoryId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}): Promise<PendingExpenseListResponse> => {
  const sp = new URLSearchParams();
  if (params?.status) sp.append('status', params.status);
  if (params?.createdBy) sp.append('createdBy', params.createdBy);
  if (params?.batchId) sp.append('batchId', params.batchId);
  if (params?.categoryId) sp.append('categoryId', params.categoryId);
  if (params?.subCategoryId) sp.append('subCategoryId', params.subCategoryId);
  if (params?.search) sp.append('search', params.search);
  if (params?.dateFrom) sp.append('dateFrom', params.dateFrom);
  if (params?.dateTo) sp.append('dateTo', params.dateTo);
  if (params?.page) sp.append('page', params.page.toString());
  if (params?.limit) sp.append('limit', params.limit.toString());

  const response = await api.get<ApiResponse<PendingExpenseListResponse>>(`/expenses/pending?${sp.toString()}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const approveExpense = async (id: string): Promise<Expense> => {
  const response = await api.put<ApiResponse<Expense>>(`/expenses/${id}/approve`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const rejectExpense = async (id: string, rejectionReason: string): Promise<Expense> => {
  const response = await api.put<ApiResponse<Expense>>(`/expenses/${id}/reject`, { rejectionReason });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const bulkApprove = async (ids: string[]): Promise<{ approved: number; requested: number }> => {
  const response = await api.put<ApiResponse<{ approved: number; requested: number }>>('/expenses/bulk-approve', { ids });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const bulkReject = async (ids: string[], rejectionReason: string): Promise<{ rejected: number; requested: number }> => {
  const response = await api.put<ApiResponse<{ rejected: number; requested: number }>>('/expenses/bulk-reject', { ids, rejectionReason });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

// ─── Reversal ────────────────────────────────────────────────────────────────

export interface BatchReverseResult {
  reversed: number;
  requested: number;
  failed: Array<{ id: string; error: string }>;
  batchId: string;
}

// Selection-based reversal returns the same per-row summary as the batch
// endpoint but without a batchId (the selection isn't a batch).
export type SelectedReverseResult = Omit<BatchReverseResult, 'batchId'>;

export const reverseExpense = async (id: string, reason: string): Promise<void> => {
  const response = await api.post<ApiResponse>(`/expenses/${id}/reverse`, { reason });
  if (!response.data.success) throw new Error(response.data.message);
};

export const reverseBatch = async (batchId: string, reason: string): Promise<BatchReverseResult> => {
  const response = await api.post<ApiResponse<BatchReverseResult>>(
    `/expenses/batches/${encodeURIComponent(batchId)}/reverse`,
    { reason },
  );
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const reverseSelected = async (ids: string[], reason: string): Promise<SelectedReverseResult> => {
  const response = await api.post<ApiResponse<SelectedReverseResult>>(
    `/expenses/reverse-selected`,
    { ids, reason },
  );
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export interface ExportExpenseParams {
  status?: string;
  createdBy?: string;
  batchId?: string;
  categoryId?: string;
  subCategoryId?: string;
  dateFrom?: string;
  dateTo?: string;
  categoryName?: string;
  subCategoryName?: string;
  submittedByName?: string;
}

const buildExportParams = (params?: ExportExpenseParams): string => {
  const sp = new URLSearchParams();
  if (params?.status) sp.append('status', params.status);
  if (params?.createdBy) sp.append('createdBy', params.createdBy);
  if (params?.batchId) sp.append('batchId', params.batchId);
  if (params?.categoryId) sp.append('categoryId', params.categoryId);
  if (params?.subCategoryId) sp.append('subCategoryId', params.subCategoryId);
  if (params?.dateFrom) sp.append('dateFrom', params.dateFrom);
  if (params?.dateTo) sp.append('dateTo', params.dateTo);
  if (params?.categoryName) sp.append('categoryName', params.categoryName);
  if (params?.subCategoryName) sp.append('subCategoryName', params.subCategoryName);
  if (params?.submittedByName) sp.append('submittedByName', params.submittedByName);
  return sp.toString();
};

/** The filtered expenses with their totals, for the Expense Report the browser prints. */
export interface ExpenseReport {
  /** "Status: APPROVED  •  From: 01 Oct 2026"; null when nothing is filtered. */
  filterSummary: string | null;
  rows: Array<{
    title: string;
    amount: number;
    date: string;
    category: string | null;
    subCategory: string | null;
    submittedBy: string | null;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
  }>;
  totals: {
    claimed: number;
    approved: { count: number; amount: number };
    pending: { count: number; amount: number };
    rejected: { count: number };
  };
}

export const getExpenseReport = async (params?: ExportExpenseParams): Promise<ExpenseReport> => {
  const response = await api.get<ApiResponse<ExpenseReport>>(`/expenses/export/report?${buildExportParams(params)}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const exportExpensesCSV = (params?: ExportExpenseParams): Promise<void> =>
  fetchAndSave(() => api.get(`/expenses/export/csv?${buildExportParams(params)}`, { responseType: 'blob' }), 'Expense_Report.csv');

const expenseService = {
  getExpenses,
  getExpenseById,
  createExpense,
  updateExpense,
  deleteExpense,
  bulkCreateRows,
  getExpenseStats,
  getApprovalContext,
  getPendingExpenses,
  approveExpense,
  rejectExpense,
  bulkApprove,
  bulkReject,
  reverseExpense,
  reverseBatch,
  reverseSelected,
  getExpenseReport,
  exportExpensesCSV,
};

export default expenseService;
